import { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Check, Close, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { ProductionMethod, RecipeV2Detail } from '../types';

interface OverviewTabProps {
  recipe: RecipeV2Detail;
  editable: boolean;
  onSaveNarrative: (value: string) => Promise<void>;
}

const Field = ({ label, value }: { label: string; value: string }) => (
  <Section style={{ p: 4, mb: 0, height: '100%' }}>
    <Stack spacing={0.75}>
      <Typography variant="textXS"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}:</Typography>
      <Typography variant="textMD"
        fontWeight={600}
        sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Stack>
  </Section>
);

/**
 * Overview tab: read-only recipe header metadata (plus the one editable field, narrative
 * description) shown before the detailed tabs - reuses the page's existing save flow via
 * onMetadataChange rather than its own API call.
 */
const OverviewTab = ({ recipe, editable, onSaveNarrative }: OverviewTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const [isEditingNarrative, setIsEditingNarrative] = useState(false);
  const [isSavingNarrative, setIsSavingNarrative] = useState(false);
  const [narrativeDraft, setNarrativeDraft] = useState(recipe.narrativeDescription ?? '');
  const [productionMethods, setProductionMethods] = useState<ProductionMethod[]>([]);

  useEffect(() => {
    if (!isEditingNarrative) setNarrativeDraft(recipe.narrativeDescription ?? '');
  }, [recipe.narrativeDescription, isEditingNarrative]);

  useEffect(() => {
    api.getProductionMethods(recipe.franchiseId).then(setProductionMethods).catch(() => setProductionMethods([]));
  }, [api, recipe.franchiseId]);

  const primaryYield = [...(recipe.yields ?? [])].sort((left, right) => left.displayOrder - right.displayOrder)[0];
  const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString() : '');
  const productionMethodName = recipe.productionMethod?.name
    ?? productionMethods.find((method) => method.id === recipe.productionMethodId)?.name
    ?? '';

  const saveNarrative = async () => {
    setIsSavingNarrative(true);
    try {
      await onSaveNarrative(narrativeDraft);
      setIsEditingNarrative(false);
    } finally {
      setIsSavingNarrative(false);
    }
  };

  const cancelNarrative = () => {
    setNarrativeDraft(recipe.narrativeDescription ?? '');
    setIsEditingNarrative(false);
  };

  const fields: { label: string; value: string }[] = [
    { label: t('name'), value: recipe.description },
    { label: t('sku'), value: recipe.itemNumber },
    { label: t('category'), value: recipe.primaryCategory },
    { label: t('build.productionMethod', 'Production Method'), value: productionMethodName },
    { label: t('yield'), value: primaryYield ? `${primaryYield.quantity} ${primaryYield.uom}` : '' },
    { label: t('build.effectiveFrom', 'Effective From'), value: formatDate(recipe.effectiveFrom) },
    { label: t('build.effectiveTo', 'Effective To (optional)'), value: formatDate(recipe.effectiveTo) },
    { label: t('version'), value: `v${recipe.currentVersion ?? 1}` },
  ];

  return (
    <Stack spacing={5}>
      <Grid container
        spacing={3}>
        {fields.map((field) => (
          <Grid item
            key={field.label}
            xs={12}
            sm={6}
            md={4}
            lg={3}>
            <Field label={field.label}
              value={field.value} />
          </Grid>
        ))}
      </Grid>

      <Section style={{ p: 4, mb: 0 }}>
        <Stack direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}>
          <Typography variant="textSM"
            fontWeight={600}>{t('create.recipeDescription', 'Description')}</Typography>
          {editable && !isEditingNarrative && (
            <IconButton size="small"
              aria-label={t('actions.edit')}
              onClick={() => setIsEditingNarrative(true)}>
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {isEditingNarrative ? (
          <Stack spacing={2}>
            <TextField
              value={narrativeDraft}
              onChange={(event) => setNarrativeDraft(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder={t('create.recipeDescriptionPlaceholder', 'e.g. Notes on preparation, flavour profile, or serving suggestions')}
            />
            <Stack direction="row"
              spacing={1}>
              <IconButton size="small"
                color="primary"
                disabled={isSavingNarrative}
                aria-label={t('actions.save')}
                onClick={saveNarrative}>
                <Check fontSize="small" />
              </IconButton>
              <IconButton size="small"
                disabled={isSavingNarrative}
                aria-label={t('actions.cancel')}
                onClick={cancelNarrative}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <Typography variant="textSM"
            color={recipe.narrativeDescription ? 'text.primary' : 'text.secondary'}>
            {recipe.narrativeDescription || t('create.recipeDescriptionEmpty', 'No description provided.')}
          </Typography>
        )}
      </Section>
    </Stack>
  );
};

export default OverviewTab;
